import {
  useApolloClient,
  useSubscription as useApolloSubscription,
} from "@apollo/client";
import { get } from "lodash";
import { useMemo, useState } from "react";
import { useQuery } from "shared/hooks/useApi";
import { CALLS } from "utils/api/graphql/queries/call";
import { CONTACT } from "utils/api/graphql/queries/contacts";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { USER_ACTIVITY } from "utils/api/graphql/queries/user-activity";
import graphql from "utils/api/graphql";
import {
  buildContactActivityBuckets,
  countQualificationActivitiesFromQualifiers,
} from "../utils/buildContactActivitiesList";

const CALLS_PAGE_SIZE = 10;
const PROJECT_ACTIVITIES_TAKE = 150;

/**
 * Lifecycle actions stored on the UserActivity table that drive the Statuts
 * bucket (and the unified Activités timeline). Quotes / subscriptions are no
 * longer queried separately — every milestone is recorded as a UserActivity
 * row by the API (`recordEventInitiator`, distribution / takeover handlers,
 * signature webhook), so the timeline reads from a single source.
 */
const SYSTEM_ACTIVITY_ACTIONS = [
  "projectCreated",
  "quoteSent",
  "quoteFollowUpSent",
  "signatureInitiated",
  "sentToManagement",
  "projectDuplicated",
  "projectDistributed",
  "projectTakenOver",
  // "documentsSigned",
  "electronicSignatureCompleted",
  "manualSignatureCompleted",
];

const SYSTEM_ACTIONS_SET = new Set(SYSTEM_ACTIVITY_ACTIONS);

const isEmailRow = (row) =>
  get(row, "module") === "EMAIL" && get(row, "action") === "sendEmail";

const isSystemRow = (row) => SYSTEM_ACTIONS_SET.has(get(row, "action"));

/**
 * Loads the activity buckets used in the contact drawer Activities tab
 * (qualifications, statuts, appels, emails) for a given project + contact.
 *
 * Statuts + emails share a single `USER_ACTIVITY` query (one round-trip)
 * combining both filters with `OR`; rows are split client-side based on
 * `module`/`action`.
 *
 * CALLS / USER_ACTIVITY use `no-cache` so normalized writes do not merge
 * partial `Project` / `Contact` / `User` into the Apollo cache and strip
 * fields still needed by the projects list (e.g. contact name on other
 * rows).
 *
 * `activitiesTotalCount` sums API `count` for calls and project activities
 * (handles pagination) plus an in-memory count for qualifications aligned
 * with {@link buildContactActivityBuckets}.
 */
const useProjectMergedActivities = ({
  project,
  contactId,
  phoneNumber,
  skip = false,
}) => {
  const apolloClient = useApolloClient();
  const [callsDisplayCount, setCallsDisplayCount] = useState(CALLS_PAGE_SIZE);
  const skipAll = skip || !project?.id || !contactId;

  const { data: qualifiersData, loading: qualifiersLoading } = useQuery(
    QUALIFIERS,
    {
      skip: skipAll,
      variables: {
        withDeleted: true,
        where: {
          qualifications: {
            contact: { id: contactId },
          },
        },
      },
    },
  );

  const callsWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  const {
    data: callsData,
    loading: callsLoading,
    refetch: refetchCalls,
  } = useQuery(CALLS, {
    fetchPolicy: "no-cache",
    variables: {
      where: callsWhere,
      orderBy: [{ updatedDate: "DESC" }],
      take: callsDisplayCount,
    },
    skip: skipAll || (!phoneNumber && !contactId),
  });

  const subscriptionWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  const projectId = project?.id;
  const projectActivitiesCustom = useMemo(
    () => ({
      OR: [
        {
          AND: [
            { action: { in: [...SYSTEM_ACTIVITY_ACTIONS] } },
            {
              jsonbPath: {
                column: "data",
                path: ["projectId"],
                op: "=",
                value: projectId,
              },
            },
          ],
        },
        {
          AND: [
            { module: "EMAIL" },
            { action: "sendEmail" },
            {
              jsonbPath: {
                column: "data",
                path: ["input", "contactId"],
                op: "=",
                value: contactId,
              },
            },
            {
              jsonbPath: {
                column: "data",
                path: ["input", "projectId"],
                op: "=",
                value: projectId,
              },
            },
          ],
        },
      ],
    }),
    [projectId, contactId],
  );

  const {
    data: projectActivitiesData,
    loading: projectActivitiesLoading,
    refetch: refetchProjectActivities,
  } = useQuery(USER_ACTIVITY, {
    skip: skipAll,
    fetchPolicy: "no-cache",
    variables: {
      take: PROJECT_ACTIVITIES_TAKE,
      orderBy: [{ createdDate: "DESC" }],
      custom: projectActivitiesCustom,
    },
  });

  useApolloSubscription(graphql.subscriptions.CALL_ADDED, {
    variables: { where: subscriptionWhere },
    skip: skipAll || (!phoneNumber && !contactId),
    onData: () => {
      refetchCalls();
      // Refresh the contact so the call counter badge in the drawer header
      // reflects the new countedCallsCount as soon as the call is recorded.
      if (contactId) {
        apolloClient
          .query({
            query: CONTACT,
            variables: { where: { id: contactId } },
            fetchPolicy: "network-only",
          })
          .catch(() => {});
      }
    },
  });

  useApolloSubscription(graphql.subscriptions.USER_ACTIVITY_ADDED, {
    skip: skipAll,
    onData: ({ data: { data: subData } = {} }) => {
      const added = get(subData, "userActivityAdded");
      if (!added) return;
      if (isEmailRow(added)) {
        const input = get(added, "data.input", {});
        if (input.contactId !== contactId || input.projectId !== project?.id) {
          return;
        }
        refetchProjectActivities();
        return;
      }
      if (isSystemRow(added)) {
        const rowProjectId = get(added, "data.projectId");
        if (rowProjectId !== project?.id) return;
        refetchProjectActivities();
      }
    },
  });

  const isPageLoading = skipAll
    ? false
    : qualifiersLoading || callsLoading || projectActivitiesLoading;

  const { emailRows, systemRows } = useMemo(() => {
    const rows = get(projectActivitiesData, "userActivities.data", []);
    const emails = [];
    const systems = [];
    for (const row of rows) {
      if (isEmailRow(row)) emails.push(row);
      else if (isSystemRow(row)) systems.push(row);
    }
    return { emailRows: emails, systemRows: systems };
  }, [projectActivitiesData]);

  const buckets = useMemo(() => {
    const qualifiersList = qualifiersData?.qualifiers?.data ?? [];
    const callsList = callsData?.calls?.data ?? [];
    return buildContactActivityBuckets({
      qualifiers: qualifiersList,
      calls: callsList,
      emailActivityRows: emailRows,
      systemActivityRows: systemRows,
      projectAuthor: project?.author,
    });
  }, [qualifiersData, callsData, emailRows, systemRows, project]);

  const activitiesTotalCount = useMemo(() => {
    if (skipAll) return 0;
    const qualifiersList = qualifiersData?.qualifiers?.data ?? [];
    const qualificationTotal =
      countQualificationActivitiesFromQualifiers(qualifiersList);
    const callsTotal = callsData?.calls?.count ?? 0;
    const projectActivitiesTotal =
      projectActivitiesData?.userActivities?.count ?? 0;
    return qualificationTotal + callsTotal + projectActivitiesTotal;
  }, [skipAll, qualifiersData, callsData, projectActivitiesData]);

  const calls = callsData?.calls?.data || [];
  const callsTotalCount = callsData?.calls?.count || 0;
  const hasMoreCalls = calls.length < callsTotalCount;

  const handleLoadMoreCalls = () => {
    setCallsDisplayCount((prev) => prev + CALLS_PAGE_SIZE);
  };

  return {
    isPageLoading,
    activitiesTotalCount,
    ...buckets,
    hasMoreCalls,
    handleLoadMoreCalls,
  };
};

export default useProjectMergedActivities;
